import { wrapBrandKitsWithAssetSelection } from './asset-selection';
import { normalizeFrameAsset } from './frame-normalizer';
import { authHeaders } from './telegram';
import type { AssetKind, BrandKit, Channel, CurrentUser } from './types';

interface ApiFailure {
  ok: false;
  message?: string;
  error?: string;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  for (const [name, value] of Object.entries(authHeaders())) {
    headers.set(name, String(value));
  }
  const controller = new AbortController();
  const timeoutMs = init.body instanceof Blob ? 300_000 : 20_000;
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers,
      signal: init.signal ?? controller.signal,
    });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new Error('Сервер отвечает слишком долго. Закройте Mini App и откройте его снова.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
  const payload = (await response.json().catch(() => null)) as T | ApiFailure | null;
  if (
    !response.ok ||
    !payload ||
    (typeof payload === 'object' && 'ok' in payload && payload.ok === false)
  ) {
    const failure = payload as ApiFailure | null;
    throw new Error(failure?.message ?? `Ошибка сервера: ${response.status}`);
  }
  return payload as T;
}

export async function establishSession(): Promise<boolean> {
  const response = await fetch('/api/session', {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'same-origin',
  });
  if (response.status === 401) {
    return false;
  }
  const payload = (await response.json().catch(() => null)) as
    | { ok: true }
    | ApiFailure
    | null;
  if (!response.ok || !payload || payload.ok === false) {
    throw new Error(
      payload && 'message' in payload && payload.message
        ? payload.message
        : `Ошибка сервера: ${response.status}`,
    );
  }
  return true;
}

export async function reportClientError(stage: string, message: string): Promise<void> {
  await api('/api/client-errors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stage, message }),
    keepalive: true,
  });
}

export interface ConversionPerformanceReport {
  engine: 'ffmpeg-wasm';
  webCodecsAvailable: boolean;
  success: boolean;
  totalMs: number;
  stages: Array<{ name: string; durationMs: number }>;
  engineWasWarm: boolean;
  engineWarmupMs: number | null;
  source: {
    sizeBytes: number;
    durationSeconds: number;
    width: number;
    height: number;
  };
  outputBytes: number | null;
  platform: string;
}

export async function reportConversionPerformance(
  report: ConversionPerformanceReport,
): Promise<void> {
  await api('/api/performance', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(report),
    keepalive: true,
  });
}

export async function getCurrentUser(): Promise<CurrentUser> {
  const response = await api<{ ok: true; user: CurrentUser }>('/api/me');
  return response.user;
}

export async function createConversionJob(): Promise<string> {
  const response = await api<{ ok: true; jobId: string }>('/api/conversions', {
    method: 'POST',
  });
  return response.jobId;
}

export async function getBrandKits(): Promise<BrandKit[]> {
  const response = await api<{ ok: true; kits: BrandKit[] }>('/api/kits');
  return wrapBrandKitsWithAssetSelection(response.kits);
}

export async function createBrandKit(name: string): Promise<string> {
  const response = await api<{ ok: true; id: string }>('/api/kits', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  return response.id;
}

export async function deleteBrandKit(id: string): Promise<void> {
  await api(`/api/kits/${id}`, { method: 'DELETE' });
}

export async function addBrandKitMember(kitId: string, telegramId: number): Promise<void> {
  await api(`/api/kits/${kitId}/members/${telegramId}`, { method: 'POST' });
}

export async function removeBrandKitMember(kitId: string, telegramId: number): Promise<void> {
  await api(`/api/kits/${kitId}/members/${telegramId}`, { method: 'DELETE' });
}

export async function uploadBrandAsset(
  kitId: string,
  kind: AssetKind,
  file: File,
  metadata: Record<string, unknown>,
): Promise<void> {
  const prepared = kind === 'frame'
    ? await normalizeFrameAsset(file, metadata)
    : { file, metadata };
  await api(`/api/kits/${kitId}/assets/${kind}`, {
    method: 'PUT',
    headers: {
      'Content-Type': prepared.file.type || 'application/octet-stream',
      'X-File-Name': encodeURIComponent(prepared.file.name),
      'X-File-Size': String(prepared.file.size),
      'X-Media-Metadata': encodeURIComponent(JSON.stringify(prepared.metadata)),
    },
    body: prepared.file,
  });
}

export async function deleteBrandAsset(kitId: string, kind: AssetKind): Promise<void> {
  await api(`/api/kits/${kitId}/assets/${kind}`, { method: 'DELETE' });
}

export async function downloadBrandAsset(assetId: string): Promise<Blob> {
  const headers = new Headers(authHeaders());
  const response = await fetch(`/api/assets/${assetId}/content`, { headers });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as ApiFailure | null;
    throw new Error(payload?.message ?? 'Не удалось загрузить файл бренд-набора.');
  }
  return response.blob();
}

export async function getChannels(): Promise<Channel[]> {
  const response = await api<{ ok: true; channels: Channel[] }>('/api/channels');
  return response.channels;
}

export async function connectChannel(identifier: string): Promise<void> {
  await api('/api/channels', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier }),
  });
}

export async function deleteChannel(id: string): Promise<void> {
  await api(`/api/channels/${id}`, { method: 'DELETE' });
}

export async function deliverVideoNote(
  file: File,
  duration: number,
  conversionJobId: string,
  channelId?: string,
): Promise<{ remainingToday: number; target: 'private' | 'channel' }> {
  const response = await api<{
    ok: true;
    remainingToday: number;
    target: 'private' | 'channel';
  }>('/api/video-note', {
    method: 'POST',
    headers: {
      'Content-Type': 'video/mp4',
      'X-File-Size': String(file.size),
      'X-Video-Duration': duration.toFixed(3),
      'X-Conversion-Job': conversionJobId,
      ...(channelId ? { 'X-Channel-Id': channelId } : {}),
    },
    body: file,
  });
  return response;
}
